/**
 * Was nach einem Stapel Fotos übrig bleibt — und wie es lesbar wird.
 *
 * ⚠️ Der Grund, weshalb das eine eigene Datei mit eigenen Tests ist: bei bis zu 60 Fotos
 * ist die naheliegende Fassung — eine Zeile je Datei — auf einem 390-px-Telefon eine
 * Wand aus demselben Satz. Bei elf Fotos füllt sie schon den Bildschirm. Gebündelt wird
 * deshalb nach dem **Text**, nicht nach der Datei: „Keine Verbindung" ist einmal eine
 * Information und sechzig Mal ein Hindernis.
 */

/** Ein Foto, das nicht durchgekommen ist — mit dem Satz, der erklärt, warum. */
export interface Fehlschlag {
  name: string;
  text: string;
}

/** Ein Satz und alle Dateien, die ihn ausgelöst haben. */
export interface Fehlergruppe {
  text: string;
  namen: string[];
}

/**
 * Gleiche Sätze zusammenfassen, Reihenfolge des ersten Auftretens behalten.
 *
 * Die Reihenfolge bleibt, damit die Meldung oben steht, die zuerst passiert ist — bei
 * einem Netzabbruch mitten im Stapel ist das die, die erklärt, was los war.
 */
export function buendle(fehlschlaege: readonly Fehlschlag[]): Fehlergruppe[] {
  const gruppen = new Map<string, string[]>();
  for (const { name, text } of fehlschlaege) {
    const vorhanden = gruppen.get(text);
    if (vorhanden) vorhanden.push(name);
    else gruppen.set(text, [name]);
  }
  return [...gruppen].map(([text, namen]) => ({ text, namen }));
}

/**
 * Die Überschrift einer Gruppe: „37 von 60 Fotos: …" oder, bei einem einzigen,
 * der Dateiname selbst.
 *
 * Bei genau einem Foto den Namen zu nennen ist keine Sonderlocke, sondern die
 * brauchbarere Auskunft: „1 von 60 Fotos" sagt nicht, **welches**.
 */
export function ueberschrift(gruppe: Fehlergruppe, gesamt: number): string {
  if (gruppe.namen.length === 1) return `${gruppe.namen[0] ?? ""}: ${gruppe.text}`;
  return `${String(gruppe.namen.length)} von ${String(gesamt)} Fotos: ${gruppe.text}`;
}

/** Was im Fehlerbereich der Seite wirklich steht. */
export interface Fehleranzeige {
  /** Der einzelne rote Kasten oben, oder `null`. */
  einzeln: string | null;
  /** Die gebündelten roten Kästen. */
  gruppen: Fehlergruppe[];
  /** Der Kasten mit „Neu anfangen" — die einzige Stelle mit einer Handlung. */
  neuAnfangen: boolean;
}

/**
 * Wie viele rote Kästen die Seite zeigt — und welche.
 *
 * ⚠️ **Aus dem Bedienbarkeits-Review vom 24.09.2026, am Ende von Schritt 3.** Bei
 * verlorener Sitzung standen **drei** Kästen untereinander, und zwei davon trugen
 * denselben Satz: einmal als einzelner Fehler, einmal gebündelt mit „2 von 2 Fotos:"
 * davor und einem sinnlosen Aufklapper „Welche Fotos?", und erst als dritter der
 * Kasten mit „Neu anfangen". Auf 390 px ist das eine Wand aus derselben Aussage, und
 * die Wiederholung sieht nach zwei verschiedenen Problemen aus.
 *
 * Die Regel ist deshalb: **ist die Sitzung weg, bleibt nur der Kasten mit der
 * Handlung.** Die anderen beiden tragen dort ohnehin keine — „erneut versuchen" ist bei
 * verlorener Sitzung schon vorher ausgeblendet, weil es nie gelingen könnte.
 *
 * Auch der Fall gemischter Ursachen fällt so: hat ein Foto am Netz gelegen und der Rest
 * an der Sitzung, ist der Netzfehler nach dem Verlust der Sitzung nicht mehr handelbar.
 * Ihn stehen zu lassen hieße, zwei Wege anzubieten, von denen einer nicht existiert.
 *
 * Steht hier und nicht in der Komponente, damit ein Test sie lesen kann, ohne eine
 * Oberfläche zu rendern — das Projekt hat kein jsdom.
 */
export function fehleranzeige(
  einzelfehler: string | null,
  fehlschlaege: readonly Fehlschlag[],
  sitzungWeg: boolean,
): Fehleranzeige {
  if (sitzungWeg) return { einzeln: null, gruppen: [], neuAnfangen: true };
  return { einzeln: einzelfehler, gruppen: buendle(fehlschlaege), neuAnfangen: false };
}
